/**
 * App-wide connectivity state behind the "Can't reach Switchboard" banner.
 *
 * Fed by the QueryCache / MutationCache handlers in lib/queryClient.ts and by
 * the admin client's rateLimitEmitter (subscribed in ConnectivityBanner):
 *   - an error where Switchboard never answered (no response, a timeout, a
 *     502/503/504 without a body, the public transport's NETWORK /
 *     BAD_RESPONSE) → 'unreachable';
 *   - a 429 reported by the admin client → 'rateLimited';
 *   - any successful query or mutation → 'ok'.
 * Every other failure (4xx, a 500 the server explained) belongs to the area
 * that made the request and leaves this state alone.
 */

import { useSyncExternalStore } from 'react';
import { isServerUnreachableError } from '../utils/errorUtils';

export type ConnectivityState =
  | { status: 'ok' }
  | { status: 'unreachable' }
  | { status: 'rateLimited'; retryAfterSeconds?: number };

const OK: ConnectivityState = { status: 'ok' };

let state: ConnectivityState = OK;
const listeners = new Set<() => void>();

function set(next: ConnectivityState): void {
  if (next === state) return;
  state = next;
  listeners.forEach((listener) => listener());
}

/** A query or mutation failed: raise the banner only if Switchboard never answered. */
export function reportRequestError(error: unknown): void {
  if (isServerUnreachableError(error)) set({ status: 'unreachable' });
}

/** A query or mutation succeeded: Switchboard is answering again. */
export function reportRequestSuccess(): void {
  if (state.status !== 'ok') set(OK);
}

/** The admin client got a 429; `retryAfter` is the server's Retry-After in seconds. */
export function reportRateLimited(retryAfter: number): void {
  set({
    status: 'rateLimited',
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  });
}

/** Test hook: back to the initial state. */
export function resetConnectivity(): void {
  set(OK);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ConnectivityState {
  return state;
}

export function useConnectivity(): ConnectivityState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
